import type { RecommendLeadEvent } from '@app/api/RecommendChat/types';
import type { WithActiveBranchId } from '@app/api/shared';

export enum LeadsEndpoints {
  GetLeadsFilterData = 'GetLeadsFilterData',
  GetLeadById = 'GetLeadById',
  MarkLeadsAsSpam = 'MarkLeadsAsSpam',
  MarkLeadsAsAnswered = 'MarkLeadsAsAnswered',
  MarkLeadsAsAnsweredBySearchId = 'MarkLeadsAsAnsweredBySearchId',
  MarkLeadsAsUnread = 'MarkLeadsAsUnread',
  MarkLeadAsRead = 'MarkLeadAsRead',
  AssignLeadsToUser = 'AssignLeadsToUser',
  AssignLeadsToUserBySearchId = 'AssignLeadsToUserBySearchId',
  SearchLeads = 'SearchLeads',
  FastSearchLeads = 'FastSearchLeads',
  SetLeadsTags = 'SetLeadsTags',
  RemoveLeadsTags = 'RemoveLeadsTags',
  SetLeadsTagsBySearchId = 'SetLeadsTagsBySearchId',
  RemoveLeadsTagsBySearchId = 'RemoveLeadsTagsBySearchId',
  DeleteLeads = 'DeleteLeads',
  DeleteLeadsBySearchId = 'DeleteLeadsBySearchId',
  TakeLead = 'TakeLead',
  FreeLead = 'FreeLead',
  GetClientLeadForView = 'GetClientLeadForView',
  GetLeadsBySearchId = 'GetLeadsBySearchId',
  GetLeadGroupsMetrics = 'GetLeadGroupsMetrics',
  CorrectLeadsFirstChannel = 'CorrectLeadsFirstChannel',
  DeleteLeadsGroups = 'DeleteLeadsGroups',
  SearchLeadsByPhone = 'SearchLeadsByPhone',
  GetAutoReplyForClient = 'GetAutoReplyForClient',
  GetRecordSlotForClient = 'GetRecordSlotForClient',
}

export interface LeadsModelItem {
  Id: number;
  Name: string | null;
  IdS: string | null;
}

export interface LeadsModelItemEx {
  Id: number;
  Name: string | null;
  IdS: string | null;
  IsDeleted: boolean;
}

export interface LeadsModelTag {
  Id: number;
  Name: string | null;
  Color: string | null;
  IconId: string | null;
  Description: string | null;
  IsDeleted: boolean;
  IsSelected: boolean;
  IsSystem: boolean;
  SystemId: string | null;
}

export interface LeadsModelCrmTaskLite {
  Id: number;
  TaskTypeId: number;
  TaskType: string | null;
  TaskTypeIconId: string | null;
  TaskTypeColor: string | null;
  Description: string | null;
  IsOverdue: boolean;
  TimeDeadline: string;
  IsCompleted: boolean;
  TimeStart: string;
}

export interface SalesFunnelStageFilterListDto {
  Id: number;
  Name: string | null;
  Color: string | null;
  SalesFunnelId?: number;
  StageId?: number;
}

export interface ModelLead {
  RecommendChatEvent?: RecommendLeadEvent | null;
  Id: number;
  CreateTime: string;
  ClientId: number | null;
  ClientPhoto: string | null;
  BranchId: number | null;
  ClientName: string | null;
  ClientPhone: string | null;
  ClientAccountName: string | null;
  UserId: number | null;
  UserName: string | null;
  LastUpdate: string | null;
  LastMessage: string | null;
  Direction: string | null;
  UnreadMessage: string | null;
  IsNoAnswer: boolean;
  AnswerTimeInMinutes: number | null;
  NoAnswerSince: string | null;
  TimeUtc: string;
  UserAnswerTill: string | null;
  SocialIntegrationId: number | null;
  SocialIntegrationType: string | null;
  SоcialIntegrationName: string | null;
  ClientTags: LeadsModelTag[] | null;
  FunnelStages: SalesFunnelStageFilterListDto[] | null;
  SpamTime: string | null;
  ClientCrmTasks: LeadsModelCrmTaskLite[] | null;
  CurrentCrmTask: LeadsModelCrmTaskLite | null;
  // Непустое значение означает, что лид поднят по наступлению задачи: в реестре
  // вместо иконки интеграции и текста сообщения показываются иконка задачи и
  // этот текст.
  CrmTaskMessage: string | null;
  IsNoCommunication: boolean;
  FirstChannelId: string | null;
}

export interface ModelLeadFilterData {
  Channels: LeadsModelItem[] | null;
  Integrations: LeadsModelItem[] | null;
  Users: LeadsModelItem[] | null;
  Tags: LeadsModelItemEx[] | null;
  Statuses: LeadsModelItem[] | null;
  CrmTaskStatuses: LeadsModelItem[] | null;
  Branches: LeadsModelItem[] | null;
  AllUsers: LeadsModelItem[] | null;
  VisibleUsers: LeadsModelItem[] | null;
}

export interface LeadsInt32ItemsFilter {
  OuterInclude: boolean;
  InnerInclude: boolean | null;
  ItemIds: number[] | null;
}

export interface ModelLeadsSearchFilter {
  SearchTerm: string | null;
  SearchTermIn: string | null;
  DateCreateS: string | null;
  DateCreateE: string | null;
  DateActiveS: string | null;
  DateActiveE: string | null;
  UserIds: number[] | null;
  IntegrationIds: number[] | null;
  StatusIds: string[] | null;
  CrmTaskStatusIds: string[] | null;
  ChannelIds: string[] | null;
  BranchIds: number[] | null;
  TagFilters: LeadsInt32ItemsFilter[] | null;
  SalesFunnelsStages: number[] | null;
  MaxItems: number;
}

export interface ModelLeadsSearchResult {
  RecommendChatEvent?: RecommendLeadEvent | null;
  SearchId: number;
  Filter: ModelLeadsSearchFilter;
  Items: ModelLead[] | null;
  CountTotal: number;
}

export interface ModelFastLeadsSearchFilter {
  SearchTerm: string | null;
}

export interface ModelRobotLogLead {
  Id: number;
  ClientId: number | null;
  ClientName: string | null;
}

export interface ModelLeadsTags {
  LeadIds: number[] | null;
  TagIds: number[] | null;
}

export interface ModelLeadsTagsBySearchId {
  // int64: точное значение не помещается в JS number (>2^53), поэтому храним
  // строкой и сериализуем тело через json-bigint (см. api.ts)
  SearchId: string;
  TagIds: number[] | null;
}

export interface ModelLeadGroupMetrics {
  SearchId: number;
  MaxItems: number;
  TotalItems: number;
  GroupSize: number;
  CountWsConnections: number;
}

export interface ModelLeadGroupsMetrics {
  Timestamp: string;
  DatabaseId: string | null;
  Groups: ModelLeadGroupMetrics[] | null;
}

export interface ModelLeadsGroupsIds {
  SearchIds: string[] | null;
}

export interface ModelLeadSearchByPhoneResult {
  ClientId: number;
  LeadId: number;
  ClientName: string | null;
  Phone: string | null;
  BranchId: number;
  IsDeleted: boolean;
}

// Ответ клиенту, подготовленный ИИ по переписке. TraceId — идентификатор
// ответа: его возвращают в SendMessageToClient, чтобы бэк знал, что
// отправленный текст подготовил ИИ. DebugInfo объясняет, почему ответ не
// подготовился, и предназначен для разработки — пользователю не показывается.
export interface ModelAiAutoReply {
  TraceId: string | null;
  Message: string | null;
  DebugInfo: string | null;
}

// Услуга в составе слота записи, подготовленного ИИ.
export interface ModelServiceLite {
  Id: number;
  Name: string | null;
  Price: number;
  Count: number;
  DurationMins: number;
}

// Слот записи клиента, подобранный ИИ по переписке. RequestId понадобится
// при создании записи по этому слоту (отдельная задача). CanCreateRecord
// говорит, достаточно ли данных, чтобы такую запись вообще можно было создать.
// TimeS/TimeE — в часовом поясе филиала.
export interface ModelAiClientRecord {
  RequestId: string;
  CanCreateRecord: boolean;
  ClientId: number;
  NameFirst: string | null;
  NameLast: string | null;
  IsPhoneVerified: boolean;
  Phone1: string | null;
  Phone1CountryId: number;
  BranchId: number;
  BranchName: string | null;
  EmployeeId: number;
  EmployeeName: string | null;
  TimeS: string;
  TimeE: string;
  Services: ModelServiceLite[] | null;
}

export type GetLeadsFilterDataRequest = WithActiveBranchId;
export type GetLeadByIdRequest = WithActiveBranchId<{
  leadId: number;
}>;
export type MarkLeadsAsSpamRequest = WithActiveBranchId<{
  isSpam: boolean;
  data: number[];
}>;
export type MarkLeadsAsAnsweredRequest = WithActiveBranchId<{
  data: number[];
}>;
export type MarkLeadsAsAnsweredBySearchIdRequest = WithActiveBranchId<{
  // int64 в query-параметре: передаём строкой, чтобы не потерять точность
  searchId: string;
}>;
export type MarkLeadsAsUnreadRequest = WithActiveBranchId<{
  data: number[];
}>;
export type MarkLeadAsReadRequest = WithActiveBranchId<{
  leadId: number;
}>;
export type AssignLeadsToUserRequest = WithActiveBranchId<{
  forUserId: number;
  data: number[];
}>;
export type AssignLeadsToUserBySearchIdRequest = WithActiveBranchId<{
  // int64 в query-параметре: передаём строкой, чтобы не потерять точность
  searchId: string;
  forUserId: number;
}>;
export type SearchLeadsRequest = WithActiveBranchId<{
  data: ModelLeadsSearchFilter;
}>;
export type FastSearchLeadsRequest = WithActiveBranchId<{
  data: ModelFastLeadsSearchFilter;
}>;
export type SetLeadsTagsRequest = WithActiveBranchId<{
  data: ModelLeadsTags;
}>;
export type RemoveLeadsTagsRequest = WithActiveBranchId<{
  data: ModelLeadsTags;
}>;
export type SetLeadsTagsBySearchIdRequest = WithActiveBranchId<{
  data: ModelLeadsTagsBySearchId;
}>;
export type RemoveLeadsTagsBySearchIdRequest = WithActiveBranchId<{
  data: ModelLeadsTagsBySearchId;
}>;
export type DeleteLeadsRequest = WithActiveBranchId<{
  data: number[];
}>;
export type DeleteLeadsBySearchIdRequest = WithActiveBranchId<{
  // int64 в query-параметре: передаём строкой, чтобы не потерять точность
  searchId: string;
}>;
export type TakeOrFreeLeadRequest = WithActiveBranchId<{
  leadId: number;
}>;
export type GetClientLeadForViewRequest = WithActiveBranchId<{
  clientId: number;
  integrationId?: number;
}>;
export type GetLeadsBySearchIdRequest = WithActiveBranchId<{
  searchId: number;
}>;
export type GetLeadGroupsMetricsRequest = WithActiveBranchId<{
  databaseId?: string;
}>;
export type DeleteLeadsGroupsRequest = WithActiveBranchId<{
  data: ModelLeadsGroupsIds;
}>;
export type CorrectLeadsFirstChannelRequest = WithActiveBranchId;
export type SearchLeadsByPhoneRequest = WithActiveBranchId<{
  phone: string;
}>;
export type GetAutoReplyForClientRequest = WithActiveBranchId<{
  clientId: number;
}>;
export type GetRecordSlotForClientRequest = WithActiveBranchId<{
  clientId: number;
}>;

export type GetLeadsFilterDataResponse = ModelLeadFilterData;
export type GetLeadByIdResponse = ModelLead;
export type MarkLeadsAsSpamResponse = ModelLead[];
export type MarkLeadsAsAnsweredResponse = ModelLead[];
export type MarkLeadsAsAnsweredBySearchIdResponse = void;
export type MarkLeadsAsUnreadResponse = ModelLead[];
export type MarkLeadAsReadResponse = ModelLead;
export type AssignLeadsToUserResponse = ModelLead[];
export type AssignLeadsToUserBySearchIdResponse = ModelLead[];
export type SearchLeadsResponse = ModelLeadsSearchResult;
export type FastSearchLeadsResponse = ModelRobotLogLead[];
export type SetLeadsTagsResponse = void;
export type RemoveLeadsTagsResponse = void;
export type SetLeadsTagsBySearchIdResponse = void;
export type RemoveLeadsTagsBySearchIdResponse = void;
export type DeleteLeadsResponse = void;
export type DeleteLeadsBySearchIdResponse = void;
export type TakeLeadResponse = ModelLead;
export type FreeLeadResponse = ModelLead;
export type GetClientLeadForViewResponse = ModelLead;
export type GetLeadsBySearchIdResponse = ModelLeadsSearchResult;
export type GetLeadGroupsMetricsResponse = ModelLeadGroupsMetrics;
export type CorrectLeadsFirstChannelResponse = void;
export type DeleteLeadsGroupsResponse = void;
export type SearchLeadsByPhoneResponse = ModelLeadSearchByPhoneResult[];
export type GetAutoReplyForClientResponse = ModelAiAutoReply;
export type GetRecordSlotForClientResponse = ModelAiClientRecord;
